// Photo helpers for uploads: check a picked file, load it, and cut the cropped area into a JPEG.
// Shapes and sizes match the backend's ImageProcessor (products 3:4, categories 3:4 tile + 16:9 wide).

export const PRODUCT_PHOTO = { key: 'photo', label: 'Photo', aspect: 3 / 4, width: 1500, height: 2000, minWidth: 900 }
export const CATEGORY_TILE = {
    key: 'tile', label: 'Tile', aspect: 3 / 4, width: 900, height: 1200, minWidth: 600,
    hint: 'Shown on the home page and in the shop menu.'
}
export const CATEGORY_WIDE = {
    key: 'wide', label: 'Wide', aspect: 16 / 9, width: 1600, height: 900, minWidth: 900,
    hint: 'Used where a category spans the full width.'
}

const TYPES = ['image/jpeg', 'image/png', 'image/webp']
const MAX_BYTES = 15 * 1024 * 1024
const MIN_SIDE = 600

// '' when the file can be cropped, otherwise a message for the shopkeeper
export const checkPhotoFile = (file) => {
    if (!file) return 'Please choose a photo.'
    if (!TYPES.includes(file.type) && !/\.(jpe?g|png|webp)$/i.test(file.name || '')) {
        return 'Please choose a JPG, PNG or WebP photo.'
    }
    if (file.size > MAX_BYTES) return 'Photos can be up to 15 MB.'
    return ''
}

// loads a picked file; browsers turn phone photos upright (EXIF) when drawing an <img>
export const readImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
        if (Math.min(image.naturalWidth, image.naturalHeight) < MIN_SIDE) {
            URL.revokeObjectURL(url)
            reject(new Error('This photo is too small. Please use one at least 600 pixels wide.'))
            return
        }
        resolve({ image, url, width: image.naturalWidth, height: image.naturalHeight })
    }
    image.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error("This photo can't be opened. Please choose a JPG, PNG or WebP photo."))
    }
    image.src = url
})

// draws the image turned by `rotation` degrees and cuts out `area` (pixels of the turned image, as react-easy-crop
// reports them), scaled down to at most outWidth x outHeight (never enlarged). Resolves a JPEG Blob.
export const cropToBlob = (image, area, rotation, outWidth, outHeight, quality = 0.9) => new Promise((resolve, reject) => {
    const radians = (rotation * Math.PI) / 180
    const sin = Math.abs(Math.sin(radians))
    const cos = Math.abs(Math.cos(radians))
    const w = image.naturalWidth
    const h = image.naturalHeight
    const turnedW = Math.round(w * cos + h * sin)
    const turnedH = Math.round(w * sin + h * cos)

    const turned = document.createElement('canvas')
    turned.width = turnedW
    turned.height = turnedH
    const tctx = turned.getContext('2d')
    tctx.translate(turnedW / 2, turnedH / 2)
    tctx.rotate(radians)
    tctx.drawImage(image, -w / 2, -h / 2)

    const scale = Math.min(1, outWidth / area.width, outHeight / area.height)
    const out = document.createElement('canvas')
    out.width = Math.max(1, Math.round(area.width * scale))
    out.height = Math.max(1, Math.round(area.height * scale))
    const ctx = out.getContext('2d')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.fillStyle = '#ffffff' // transparent PNGs get a white background
    ctx.fillRect(0, 0, out.width, out.height)
    ctx.drawImage(turned, area.x, area.y, area.width, area.height, 0, 0, out.width, out.height)

    out.toBlob(blob => (blob ? resolve(blob) : reject(new Error('Could not prepare the photo.'))), 'image/jpeg', quality)
})

// The server's own 3:4 copies are named like list_1760000000000_k3z.jpg. Older products' list/thumbnail copies were
// squashed to fixed sizes, so for those the full photo is used instead (the browser scales it without distortion).
const PROCESSED = /\/(list_|thump_)?\d{10,}_[0-9a-z]{1,3}\.jpg$/
export const cleanCopy = (copyUrl, fullUrl) => (copyUrl && PROCESSED.test(copyUrl) ? copyUrl : fullUrl || copyUrl)

// a File the upload forms can send, named after the original
export const toJpegFile = (blob, originalName) => {
    const base = String(originalName || 'photo').replace(/\.[^.]+$/, '').replace(/[^\w-]+/g, '-').slice(0, 60) || 'photo'
    return new File([blob], base + '.jpg', { type: 'image/jpeg' })
}
